// src/pages/creator-panel/CallLogs/tabs/MessagesTab.js
// Raw-message debug inspector. Each turn rendered as a JSON card with
// role badge, timestamp, Copy button, and collapsible body that
// preserves `\n` and supports substring search w/ highlighting.
//
// We derive the messages array on the fly from `session.system_prompt`
// (the chatbot's configured prompt) + `session.transcript_turns`. We do
// NOT persist a separate messages table — this is read-only debug data.
import React, { useMemo, useState } from 'react';
import {
    Box, Card, CardContent, TextField, InputAdornment, Typography, IconButton,
    Tooltip, Button, Snackbar,
} from '@mui/material';
import {
    Search as SearchIcon,
    ContentCopy as CopyIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

const COLLAPSED_LINES = 6;

// Role → MUI theme token mapping. Per the spec: no new colors, only existing
// semantic tokens. We render a thin left accent bar per card using these.
const ROLE_ACCENT = {
    system: 'text.disabled',     // muted
    assistant: 'primary.main',
    user: 'warning.main',
    tool: 'info.main',
};

const ROLE_LABEL_COLOR = {
    system: 'text.secondary',
    assistant: 'primary.main',
    user: 'warning.main',
    tool: 'info.main',
};

const fmtHmsFromEpochMs = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const epochMsFromIso = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.getTime();
};

// Highlight all case-insensitive occurrences of `needle` inside `text`.
// Uses MUI's `<Box component="mark">` so the highlight token comes from
// the theme — no custom CSS color.
const highlightMatches = (text, needle) => {
    if (!needle) return text;
    const idx = String(text).toLowerCase().indexOf(needle.toLowerCase());
    if (idx === -1) return text;
    const parts = [];
    let cursor = 0;
    const lower = String(text).toLowerCase();
    const q = needle.toLowerCase();
    while (true) {
        const at = lower.indexOf(q, cursor);
        if (at === -1) { parts.push(String(text).slice(cursor)); break; }
        if (at > cursor) parts.push(String(text).slice(cursor, at));
        parts.push(
            <Box key={`m-${at}`} component="mark" sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', px: 0.25, borderRadius: 0.5 }}>
                {String(text).slice(at, at + needle.length)}
            </Box>
        );
        cursor = at + needle.length;
    }
    return parts;
};

// Render a JSON value with light syntax highlighting from theme tokens.
// (Reuses the same color philosophy as JsonViewer.js — keys, strings,
// numbers, brackets — but inline-styled for the message-body code block.)
const SYNTAX = {
    key: '#0369a1', string: '#15803d', number: '#b45309',
    boolean: '#be185d', null: '#64748b', bracket: '#475569',
};

const renderJson = (obj, search) => {
    // We stringify to a known-good pretty form first, then highlight
    // tokens with a regex pass. Simple, no parser needed.
    const pretty = JSON.stringify(obj, null, 2);
    // Tokenize: strings, keys (string followed by colon), numbers, booleans, brackets, punctuation.
    const tokenRe = /("(?:\\.|[^"\\])*")(\s*:)?|(\btrue\b|\bfalse\b|\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}\[\],])/g;
    const out = [];
    let last = 0;
    let lineKey = 0;
    pretty.replace(tokenRe, (m, str, isKey, bool, num, brack, offset) => {
        if (offset > last) out.push(pretty.slice(last, offset));
        if (str !== undefined) {
            if (isKey) {
                out.push(<span key={lineKey++} style={{ color: SYNTAX.key }}>{str}</span>);
                out.push(<span key={lineKey++} style={{ color: SYNTAX.bracket }}>:</span>);
            } else {
                // For the `message` string we want to preserve \n as real line
                // breaks. JSON.stringify escapes them, so we re-render the inner
                // text replacing literal \\n with newlines for visual fidelity.
                const inner = str.slice(1, -1).replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
                out.push(
                    <span key={lineKey++} style={{ color: SYNTAX.string }}>
                        "{search ? highlightMatches(inner, search) : inner}"
                    </span>
                );
            }
        } else if (bool !== undefined) {
            out.push(<span key={lineKey++} style={{ color: SYNTAX.boolean }}>{bool}</span>);
        } else if (num !== undefined) {
            out.push(<span key={lineKey++} style={{ color: SYNTAX.number }}>{num}</span>);
        } else if (brack !== undefined) {
            out.push(<span key={lineKey++} style={{ color: SYNTAX.bracket }}>{brack}</span>);
        }
        last = offset + m.length;
        return m;
    });
    if (last < pretty.length) out.push(pretty.slice(last));
    return out;
};

const MessageCard = ({ msg, search }) => {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const pretty = useMemo(() => JSON.stringify({
        role: msg.role,
        time: msg.time,
        message: msg.message,
    }, null, 2), [msg]);

    // Decide whether to show the expand toggle: only when the prettified
    // JSON is taller than COLLAPSED_LINES rows.
    const lineCount = pretty.split('\n').length;
    const needsToggle = lineCount > COLLAPSED_LINES;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(pretty);
            setCopied(true);
        } catch (e) { /* clipboard blocked */ }
    };

    return (
        <Card variant="outlined" sx={{ mb: 1.5, borderLeft: '3px solid', borderLeftColor: ROLE_ACCENT[msg.role] || 'divider' }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Message {msg.index}</Typography>
                    <Typography variant="caption" sx={{ color: ROLE_LABEL_COLOR[msg.role] || 'text.secondary', fontWeight: 600 }}>
                        {msg.role}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace' }}>
                        {fmtHmsFromEpochMs(msg.time)}
                    </Typography>
                </Box>
                <Tooltip title={copied ? 'Copied' : 'Copy JSON'}>
                    <Button size="small" startIcon={<CopyIcon fontSize="small" />} onClick={handleCopy}>
                        Copy
                    </Button>
                </Tooltip>
            </Box>
            <CardContent sx={{ pt: 1.5, pb: 1, '&:last-child': { pb: 1.5 } }}>
                <Box
                    component="pre"
                    sx={{
                        margin: 0,
                        fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        maxHeight: open || !needsToggle ? 'none' : `${COLLAPSED_LINES * 1.6}em`,
                        overflow: 'hidden',
                        color: 'text.primary',
                    }}
                >
                    {renderJson({ role: msg.role, time: msg.time, message: msg.message }, search)}
                </Box>
                {needsToggle && (
                    <Button
                        size="small"
                        onClick={() => setOpen(o => !o)}
                        startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ mt: 0.5 }}
                    >
                        {open ? 'Show less' : 'Show more'}
                    </Button>
                )}
            </CardContent>
            <Snackbar
                open={copied}
                autoHideDuration={1500}
                onClose={() => setCopied(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                message="Copied"
            />
        </Card>
    );
};

const MessagesTab = ({ session }) => {
    const [search, setSearch] = useState('');

    // Derive messages: synthetic system prompt as Message 1 if present,
    // then transcript turns in order.
    const messages = useMemo(() => {
        const out = [];
        const startMs = session?.started_at ? new Date(session.started_at).getTime() : Date.now();
        if (session?.system_prompt) {
            out.push({
                index: out.length + 1,
                role: 'system',
                time: startMs,
                message: session.system_prompt,
            });
        }
        for (const t of (session?.transcript_turns || [])) {
            const offsetMs = (t.offset_seconds || 0) * 1000;
            out.push({
                index: out.length + 1,
                role: t.role === 'assistant' ? 'assistant' : 'user',
                time: startMs + offsetMs,
                message: t.text || '',
            });
        }
        return out;
    }, [session?.system_prompt, session?.transcript_turns, session?.started_at]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return messages;
        return messages.filter(m =>
            (m.role || '').toLowerCase().includes(q) ||
            String(m.message || '').toLowerCase().includes(q)
        );
    }, [messages, search]);

    return (
        <Box>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', pb: 1.5 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search in messages..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    }}
                />
            </Box>

            {filtered.length === 0 ? (
                <Typography sx={{ color: 'text.disabled', fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                    {messages.length === 0 ? 'No messages for this call.' : 'No messages match your search.'}
                </Typography>
            ) : (
                filtered.map(m => <MessageCard key={m.index} msg={m} search={search} />)
            )}
        </Box>
    );
};

export default MessagesTab;
